import React, { forwardRef } from 'react';
import {
  AttendanceTable,
  CoverPage,
  EvaluatorTable,
  FeedbackSection,
  PageFooter,
  PageHeader,
  PrePostTable,
  ProgramSchedule,
  ReportSection,
  SectionHeader,
  StatisticsSection,
  TableOfContents,
  TraineeTable
} from './ReportSection';
import { backendURL } from 'redux/reducers/settings';

interface RoutineReportProps {
  data: any;
}

// Height reserved at the bottom of every sheet for our own footer. Kept under
// ~10mm on purpose: Chrome only paints its own header/footer (title, url) when
// the page margin is large enough to fit it.
const FOOTER_HEIGHT_MM = 8;
// A4 portrait height minus the footer strip.
const PAGE_CONTENT_HEIGHT = `${297 - FOOTER_HEIGHT_MM}mm`;

// Breathing room repeated at the top/bottom of every content sheet.
const TOP_GAP = '15mm';
const BOTTOM_GAP = '10mm';

// Tallest an image may be and still fit on a single sheet:
// 297mm paper - 8mm footer - 15mm top gap - 10mm bottom gap, minus a little
// slack for the heading that usually sits above it.
const MAX_IMAGE_HEIGHT = '230mm';

// The side/top margins live on the content (not on @page) so the browser does
// not print its own header/footer (title, url) around the page.
const PAGE_STYLE: React.CSSProperties = {
  padding: '15mm',
  backgroundColor: 'white',
  boxSizing: 'border-box'
};

const NewRoutineReport = forwardRef<HTMLDivElement, RoutineReportProps>(({ data }, ref) => {
  if (!data) return <div style={{ padding: '2px', color: '#888' }}>No data available</div>;

  const generatedOn = new Date().toLocaleString('en-US', {
    month: 'short',
    day: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });

  const hasParticipants = data.trainees?.length > 0;
  const hasModules = data.modules?.length > 0;
  const hasEvaluations = data.evaluator_evaluations?.some((e: any) => e.total_marks > 0);

  return (
    <div
      ref={ref}
      className="routine-report"
      style={{
        backgroundColor: 'white',
        fontFamily: 'Arial, Helvetica, sans-serif',
        color: '#333',
        maxWidth: '290mm',
        margin: '0 auto',
        fontSize: '13px'
      }}>

      {/* ── PAGE 1: COVER (nothing else on this page) ── */}
      <div
        style={{
          ...PAGE_STYLE,
          height: PAGE_CONTENT_HEIGHT,
          overflow: 'hidden',
          pageBreakAfter: 'always',
          breakAfter: 'page'
        }}>
        {/* <PageHeader data={data} /> */}
        <CoverPage data={data} />
        {/* <PageFooter data={data} /> */}
      </div>

      {/* ── PAGE 2: TABLE OF CONTENTS ── */}
      <div style={{ ...PAGE_STYLE, pageBreakAfter: 'always', breakAfter: 'page' }}>
        {/* <PageHeader data={data} /> */}
        <TableOfContents data={data} />
        {/* <PageFooter data={data} /> */}
      </div>

      {/* ── CONTENT PAGES ──
          The content flows over many sheets, and padding on the wrapper only
          applies to its first/last fragment — every page in between would start
          flush against the paper edge. A table gives us repeating spacers: the
          <thead> row is redrawn at the top of every page and the <tfoot> row at
          the bottom, so each sheet keeps the same top/bottom gap. */}
      <div style={{ ...PAGE_STYLE, paddingTop: 0, paddingBottom: 0 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <td style={{ height: TOP_GAP }} />
            </tr>
          </thead>
          <tfoot>
            <tr>
              <td style={{ height: BOTTOM_GAP }} />
            </tr>
          </tfoot>
          <tbody>
            <tr>
              <td style={{ verticalAlign: 'top' }}>
                {/* <PageHeader data={data} /> */}

                {/* 1. Introduction */}
                <ReportSection title="1. Introduction" content={data.introduction} />

                {/* 2. Training Details */}
                <ReportSection title="2. Training Details" content={data.training_details} />

                {/* 2.1 Participants Details */}
                {hasParticipants && (
                  <section style={{ marginBottom: '32px' }}>
                    <SectionHeader title="2.1 Participants Details" />
                    <TraineeTable trainees={data.trainees} />
                  </section>
                )}

                {/* 3. Opening Ceremony */}
                <ReportSection
                  title="3. Opening Ceremony"
                  content={data.opening_ceremony}
                  image={data.start_training_image_path}
                  baseUrl={backendURL}
                />

                {/* 4. Program Schedule */}
                {hasModules && (
                  <section style={{ marginBottom: '32px' }}>
                    <SectionHeader title="4 Program Schedule" />
                    <ProgramSchedule modules={data.modules} />
                  </section>
                )}

                {/* 5. Feedback Report */}
                <FeedbackSection data={data} />

                {/* 6. Attendance Report */}
                {hasParticipants && (
                  <section style={{ marginBottom: '32px' }}>
                    <SectionHeader title="6 Attendance Report" />
                    <AttendanceTable trainees={data.trainees} />
                  </section>
                )}

                {/* 7. Pre/Post Evaluation Report */}
                {data.trainees?.[0]?.post_evaluation?.length && (
                  <section style={{ marginBottom: '32px' }}>
                    <SectionHeader title="7 Pre/Post Evaluation Report" />
                    <PrePostTable trainees={data.trainees} />
                  </section>
                )}

                {/* 8. Statistics */}
                {data.stats && (
                  <section style={{ marginBottom: '32px' }}>
                    <SectionHeader title="8 Statistics" />
                    <StatisticsSection stats={data.stats} />
                  </section>
                )}

                {/* 9. Closing Ceremony */}
                <ReportSection
                  title="9. Closing Ceremony"
                  content={data.closing_ceremony}
                  image={data.end_training_image_path}
                  baseUrl={backendURL}
                />

                {/* 10. Coordinators' Remarks */}
                <ReportSection title="10. Coordinators' Remarks" content={data.coordinator_remarks} />

                {/* 11. Additional Information */}
                <ReportSection title="11. Additional Information" content={data.additional_information} />

                {/* Annex */}
                <ReportSection title="Annex" content={data.annex} />

                {/* Optional: Evaluator Evaluation (if data present) */}
                {hasEvaluations && (
                  <section style={{ marginBottom: '32px' }}>
                    <SectionHeader title="Evaluator Evaluation Report" />
                    <EvaluatorTable evaluations={data.evaluator_evaluations} />
                  </section>
                )}

                <PageFooter data={data} />
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <style>{`
        /* Images come from the rich-text editor, so they carry whatever width
           and height the author left on them - often far wider than the A4
           content column, which made them run off the right edge of the sheet.
           Clamp every image to the column width and to a single page's height,
           and keep it from being split across a page break. */
        .routine-report img {
          max-width: 100% !important;
          height: auto !important;
          max-height: ${MAX_IMAGE_HEIGHT};
          object-fit: contain;
          break-inside: avoid;
          page-break-inside: avoid;
        }
        .routine-report figure,
        .routine-report .rich-content p:has(> img),
        .routine-report .rich-content figure {
          break-inside: avoid;
          page-break-inside: avoid;
          max-width: 100%;
          margin-left: 0;
          margin-right: 0;
        }
        /* Editor content can also carry wide tables and unbreakable strings. */
        .routine-report .rich-content {
          overflow-wrap: break-word;
          word-wrap: break-word;
        }
        .routine-report .rich-content table {
          max-width: 100% !important;
          width: 100% !important;
          table-layout: fixed;
          border-collapse: collapse;
        }
        .routine-report .rich-content td,
        .routine-report .rich-content th {
          overflow-wrap: break-word;
          word-wrap: break-word;
        }

        /* Our own running footer: "Generated on ..." on the left, page number on
           the right. The browser's default header/footer (title, url) stays off
           because we never leave a top/side page margin for it. */
        @page {
          size: A4 portrait;
          margin: 0 0 ${FOOTER_HEIGHT_MM}mm;

          @bottom-left {
            content: "Page " counter(page) " of " counter(pages);
            font-family: Arial, Helvetica, sans-serif;
            font-size: 10px;
            color: #757575;
            padding: 2mm 0 0 15mm;
            vertical-align: top;
            }
            
            @bottom-right {
              content: "Generated on ${generatedOn}";
            font-family: Arial, Helvetica, sans-serif;
            font-size: 10px;
            color: #757575;
            padding: 2mm 15mm 0 0;
            vertical-align: top;
          }
        }
        @media print {
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        }
      `}</style>
    </div>
  );
});

NewRoutineReport.displayName = 'NewRoutineReport';

export default NewRoutineReport;
